import { Box } from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import { useShowContext } from 'framework/show';
import { useAuthorization } from 'framework/authorization';
import {
  READ_EMPLOYEE_ACCOUNTS_1C_ID,
  READ_EMPLOYEE_ACCOUNTS_AZURE_ID,
  UPDATE_EMPLOYEE_ACCOUNTS_1C_ID,
  UPDATE_EMPLOYEE_ACCOUNTS_AZURE_ID,
} from 'domain/shared/employee';
import { ShowSection, MediumFormRow } from 'framework/ui/layout';
import { useEffect } from 'react';
import { TextInput, useFormShowContext } from 'framework/form';
import EditFormShowButton from '../../../shared/button/showEdit/EditFormShowButton';
import FormActionButtons from '../../../shared/button/showEdit/FormActionButtons';
import SectionField from './SectionField';
import { AccountsContentProps } from './types';
import { useMessageService } from 'framework/ui/Message';

const AccountsContent: React.FC<AccountsContentProps> = ({ employeeId }) => {
  const { data: employee } = useShowContext();
  const { isGranted } = useAuthorization();
  const { isEditMode } = useFormShowContext();
  const { cleanMessage } = useMessageService('employeeAccounts');

  const canReadAzureId = isGranted(READ_EMPLOYEE_ACCOUNTS_AZURE_ID);
  const canRead1CId = isGranted(READ_EMPLOYEE_ACCOUNTS_1C_ID);

  useEffect(() => {
    if (!isEditMode) {
      cleanMessage();
    }
  }, [isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canReadAzureId && !canRead1CId) {
    return null;
  }

  const azureId = employee?.getValueBySource('azureId');
  const oneCId = employee?.getValueBySource('extendedInformation.financialInformation.financialOneCId');

  const renderActions = () => {
    if (!isGranted([UPDATE_EMPLOYEE_ACCOUNTS_1C_ID, UPDATE_EMPLOYEE_ACCOUNTS_AZURE_ID])) {
      return null;
    }

    if (isEditMode) {
      return <FormActionButtons />;
    }

    return (
      <EditFormShowButton requiredPermission={[UPDATE_EMPLOYEE_ACCOUNTS_1C_ID, UPDATE_EMPLOYEE_ACCOUNTS_AZURE_ID]} />
    );
  };

  return (
    <ShowSection
      sx={{
        position: 'relative',
        '.show-title-container': {
          marginBottom: '20px',
        },
      }}
      title={
        <AccountCircle
          sx={{
            color: 'text.primary',
          }}
        />
      }
      actions={
        <Box
          sx={{
            position: 'absolute',
            right: '-15px',
            top: '-4px',
            button: {
              m: 0,
            },
          }}
        >
          {renderActions()}
        </Box>
      }
      disableDefaultPadding
    >
      <Box>
        {isEditMode ? (
          <Box>
            <MediumFormRow>
              {canRead1CId && (
                <TextInput
                  source="extendedInformation.financialInformation.financialOneCId"
                  label="1C ID"
                  placeholder="Enter 1C ID"
                />
              )}
              {canReadAzureId && <TextInput source="azureId" label="Azure ID" disabled />}
            </MediumFormRow>
          </Box>
        ) : (
          <Box display="grid" gridTemplateColumns="minmax(150px, 0.33fr) 1fr" gap={3}>
            {canRead1CId && <SectionField label="1C ID">{oneCId || '—'}</SectionField>}
            {canReadAzureId && <SectionField label="Azure ID">{azureId || '—'}</SectionField>}
          </Box>
        )}
      </Box>
    </ShowSection>
  );
};

export default AccountsContent;
